import React, { useMemo } from 'react';
import { Moment } from 'moment';
import { AccessorKeyColumnDef, createColumnHelper, RowData } from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';
import {
	CERTIFICATES_ENABLED,
	CODE_95_ENABLED,
	useViewPreferences,
} from '../../../../hooks/view-preferences';
import Table from './Table';
import { compareDates } from '../../../../utils/sort';
import moment from '../../../../moment';
import { ExpirationDateCell, RiskCell, StartDateCell } from './TableCells';

export type EnrollmentDataEntry = {
	enrollment: {
		certificateDateManuallyUpdated: boolean;
		certificateExpirationDate: string;
		code95Hours: number;
		id: number;
		excludeFromRiskCalculation: boolean;
		student: { id: number };
		training: {
			id: number;
			name: string;
			theory: boolean;
			start: string;
			city: string;
			external: boolean;
			slots: Array<{
				students: { id: number }[];
			}>;
		};
	};
	certificationRisk: number | null;
	inCurrentCycle: string;
	sortDate: string;
	startDate: Moment;
};

const columnHelper = createColumnHelper<EnrollmentDataEntry>();
type Props = {
	enrollments: EnrollmentDataEntry[];
	isCode95Student: boolean;
	handleRowClick: (row: RowData) => void;
};
const EnrollmentOverview = ({ enrollments, isCode95Student, handleRowClick }: Props) => {
	const { isPreferenceOptionEnabled } = useViewPreferences();
	const { t } = useTranslation();

	const columns = useMemo(
		() =>
			[
				columnHelper.accessor('enrollment.training.name', {
					header: t('enrollments.trainingName'),
				}),
				columnHelper.accessor('startDate', {
					header: t('enrollments.date'),
					sortingFn: (rowA, rowB) =>
						compareDates(moment(rowA.original.startDate), moment(rowB.original.startDate)),
					cell: StartDateCell,
				}),
				columnHelper.accessor('enrollment.training.city', {
					header: t('enrollments.city'),
				}),
				...(isPreferenceOptionEnabled(CODE_95_ENABLED)
					? [
							columnHelper.accessor('enrollment.code95Hours', {
								header: t('enrollments.ccvHours'),
							}),
							columnHelper.accessor('enrollment.training.theory', {
								header: t('enrollments.trainingType'),
								cell: (cellInfo) =>
									cellInfo.cell.getValue()
										? t('enrollments.theoryTraining')
										: t('enrollments.practiceTraining'),
							}),
						]
					: []),
				...(isPreferenceOptionEnabled(CERTIFICATES_ENABLED)
					? [
							columnHelper.accessor('certificationRisk', {
								header: t('employees.risk'),
								cell: RiskCell,
							}),
							columnHelper.accessor('enrollment.certificateExpirationDate', {
								header: t('enrollments.validUntil'),
								cell: ExpirationDateCell,
								sortingFn: (rowA, rowB) =>
									compareDates(
										moment(rowA.original.enrollment.certificateExpirationDate),
										moment(rowB.original.enrollment.certificateExpirationDate),
									),
							}),
						]
					: []),
				...(isCode95Student
					? [
							columnHelper.accessor('inCurrentCycle', {
								header: t('enrollments.currentCycle'),
							}),
						]
					: []),
			] as Array<AccessorKeyColumnDef<EnrollmentDataEntry, unknown>>,
		[],
	);
	return <Table data={enrollments} columns={columns} handleRowClick={handleRowClick} />;
};

export default EnrollmentOverview;
